import CurrentUser from "./apiResources/CurrentUser";
import Employer from "./apiResources/Employer";
import EmployerUser from "./apiResources/EmployerUser";
import Job from "./apiResources/Job";
import Engagement from "./apiResources/Engagement";
import { FilterData as JobListFilterData } from "./components/EmployerJobsIndex/JobListPage";
import { FilterData as EngagementListFilterData } from "./components/AgencyEngagementsIndex/EngagementsListPage";
import { FilterData as CandidateListFilterData } from "./components/CandidatesListPage";

type JobListCache = {
  query?: string;
  filters?: JobListFilterData;
  page?: number;
  jobs?: Job[];
  totalJobs?: number;
  user?: CurrentUser;
  companies?: Employer[];
  users?: EmployerUser[];
};

type EngagementListCache = {
  query: string;
  filters: EngagementListFilterData;
  page: number;
  results: Engagement[];
  total: number;
  user: CurrentUser;
};

type CandidateListCache = {
  query: string;
  filters: CandidateListFilterData;
  page: number;
  total: number;
};

type CacheStore = {
  "job-list": JobListCache;
  "engagement-list": EngagementListCache;
  "candidate-list": CandidateListCache;
};

type CacheStoreKey = keyof CacheStore;

type CacheStoreItem<T> = {
  value: T;
  expiry: number;
};

const fiveMinutes = 300000;

const name = (key: string) => `cache:${key}`;

const deleteCache = <T extends CacheStoreKey>(key: T) => {
  localStorage.removeItem(name(key));
};

const getCache = <T extends CacheStoreKey>(key: T) => {
  const rawItem = localStorage.getItem(name(key));

  if (!rawItem) {
    return null;
  }

  const item = JSON.parse(rawItem) as CacheStoreItem<CacheStore[T]>;

  const now = new Date();

  if (now.getTime() > item.expiry) {
    deleteCache(key);
    return null;
  }

  return item.value;
};

const setCache = <T extends CacheStoreKey>(
  key: T,
  value: CacheStore[T],
  ttl: number = fiveMinutes
) => {
  const now = new Date();

  const item = {
    value,
    expiry: now.getTime() + ttl,
  } as CacheStoreItem<CacheStore[T]>;

  localStorage.setItem(name(key), JSON.stringify(item));
};

/**
 * Delete all the cached data.
 */
const deleteEntireCache = () => {
  // This might seem a bit over complicated (explicitly declaring an object and
  // then just iterating over the keys), however we are doing this  so that when
  // a new key is added to the cache store type it will throw a typescript error
  // if it's not also added here.
  const cacheData: CacheStore = {
    "job-list": null,
    "engagement-list": null,
    "candidate-list": null,
  };

  Object.keys(cacheData).forEach((key: CacheStoreKey) => deleteCache(key));
};

const Cache = {
  get: getCache,
  set: setCache,
  delete: deleteCache,
  purge: deleteEntireCache,
};

export default Cache;
